'use client';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { type Dictionary, UnauthorizedError } from '@flemingmetrics/api-client';
import { API } from '@/api';
import { useCurrentUser, useDispatch } from '@/hooks/useStore';
import { usePath } from '@/hooks/usePath';
import { showErrorToaster, showWarningToaster } from '@/utils/toasters';
import { setCurrentUser as dispatchSetCurrentUser } from '@/store/slices/currentUserSlice';
import { setMemberCompanies as dispatchSetMemberCompanies } from '@/store/slices/companyMembersSlice';
import { setInvestorCompanies as dispatchSetInvestorCompanies } from '@/store/slices/companyInvestorsSlice';
import { setFinancialReports as dispatchSetFinancialReports } from '@/store/slices/financialReportsSlice';
import AppHeader from '@/components/AppHeader';
import CheckingAuth from '@/components/CheckingAuth';
import AppSidebar from '@/components/AppSidebar';
import { goToLogin, isInput } from '@/utils/dom';
import { useAppState } from '@/hooks/useAppState';
import { SHORTCUT_KEYS, ShortcutKey } from '@/constants/shortcutKeys';
import { DIALOGS } from '@/constants/dialogs';
import { NAVIGATION_KEYS, NavigationKey } from '@/constants/navigationKeys';
import DialogsManager from '@/components/DialogsManager';
import { isValidatedResponse } from '@/utils/users';
import {
  classes,
  fetchCurrentUserCompanies,
  getNavigation,
  getShortcut,
  isIgnoredKey,
  styles,
} from './utils';

export default function Layout({ children }: PropsWithChildren) {
  const shiftCount = useRef(0);
  const dispatch = useDispatch();
  const { navigate } = usePath();
  const currentUser = useCurrentUser();
  const { activeDialogs, setActiveDialogs, onApiError } = useAppState();

  useEffect(() => {
    handleFetchData();
  }, []);

  useEffect(() => {
    function checkKeyPress(e: KeyboardEvent) {
      return handleKeyPress(e, activeDialogs);
    }
    window.addEventListener('keyup', checkKeyPress);
    return () => {
      window.removeEventListener('keyup', checkKeyPress);
    };
  }, [activeDialogs]);

  const handleFetchData = async () => {
    try {
      const user = await API.Users.fetchCurrentUser();
      if (!user || !isValidatedResponse(user)) return goToLogin();
      const companies = await fetchCurrentUserCompanies();
      dispatch(dispatchSetCurrentUser(user));
      dispatch(dispatchSetMemberCompanies(companies.memberCompanies));
      dispatch(dispatchSetInvestorCompanies(companies.investorCompanies));
      dispatch(dispatchSetFinancialReports(companies.financialReports));
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        goToLogin(window.location.href);
      } else {
        showErrorToaster('Error fetching current user');
        throw err;
      }
    }
  };

  const handleSelectShortcut = (shortcut: ShortcutKey) => {
    let updates: Dictionary<boolean> = { [DIALOGS.SHORTCUTS]: false };
    const selection = SHORTCUT_KEYS[shortcut];
    if (selection) updates[selection] = true;
    setActiveDialogs((dialogs) => ({ ...dialogs, ...updates }));
  };

  const handleSelectNavigation = (shortcut: NavigationKey) => {
    setActiveDialogs((d) => ({
      ...d,
      [DIALOGS.NAVIGATION]: false,
    }));
    navigate(NAVIGATION_KEYS[shortcut]);
  };

  const handleKeyPress = (e: KeyboardEvent, dialogs: typeof activeDialogs) => {
    // Ignore if there's a text input actively in use
    if (isInput(e)) return null;
    const isSearchDialogShown = dialogs[DIALOGS.SEARCH];
    const isShortcutsDialogShown = dialogs[DIALOGS.SHORTCUTS];
    const isNavigationDialogShown = dialogs[DIALOGS.NAVIGATION];
    if (e.key === 'Escape' && isSearchDialogShown) {
      shiftCount.current = 0;
      return setActiveDialogs((d) => ({
        ...d,
        [DIALOGS.SEARCH]: false,
        [DIALOGS.SHORTCUTS]: false,
        [DIALOGS.NAVIGATION]: false,
      }));
    }
    // if (['\\', ':'].includes(e.key) && !isNavigationDialogShown) { TODO
    //   return setActiveDialogs((d) => ({ ...d, [DIALOGS.SHORTCUTS]: true }));
    // }
    if (e.key === '/' && !isShortcutsDialogShown) {
      return setActiveDialogs((d) => ({ ...d, [DIALOGS.NAVIGATION]: true }));
    }
    if (isShortcutsDialogShown) {
      const shortcut = getShortcut(e);
      if (shortcut) return handleSelectShortcut(shortcut);
    } else if (isNavigationDialogShown) {
      const shortcut = getNavigation(e);
      if (shortcut) return handleSelectNavigation(shortcut);
    }
    if (isShortcutsDialogShown || isNavigationDialogShown) {
      if (isIgnoredKey(e)) return; // Ignore shift key since it's probably just being used as a modifier key
      return showWarningToaster(`Unrecognized shortcut: ${e.key}`);
    }
    if (e.key !== 'Shift' || isSearchDialogShown) return;
    if (shiftCount.current === 1) {
      shiftCount.current = 0;
      setActiveDialogs((d) => ({ ...d, [DIALOGS.SEARCH]: true }));
    } else if (shiftCount.current === 0) {
      shiftCount.current = 1;
      // Stop listening if there's more than half a second between key presses
      setTimeout(() => (shiftCount.current = 0), 500);
    } else {
      shiftCount.current = 0;
    }
  };

  if (!currentUser) return <CheckingAuth />;

  return (
    <div className={classes.wrapper}>
      <DialogsManager
        onSelectShortcut={handleSelectShortcut}
        onSelectNavigation={handleSelectNavigation}
      />
      <AppHeader />
      <div className={classes.body} style={styles.body}>
        <AppSidebar />
        <div className={classes.content} style={styles.content}>
          {children}
        </div>
      </div>
    </div>
  );
}
